import { useState } from 'react';

/**
 * Theme Selection Modal - Shows when no theme preference has been set
 * Allows users to choose between light and dark mode
 */
export default function ThemeSelectionModal({ isOpen, onSelectTheme }) {
  const [selectedTheme, setSelectedTheme] = useState('dark');

  if (!isOpen) return null;

  const handleConfirm = () => {
    onSelectTheme(selectedTheme);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[10000] flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-500/20 rounded-lg" style={{ color: 'var(--color-text-on-accent)' }}>
                          <svg className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white">Choose Your Theme</h2>
          </div>
          <p className="text-gray-400 text-sm">
            Select your preferred theme. You can change this anytime in your settings.
          </p>
        </div>

        {/* Theme Options */}
        <div className="p-6 space-y-3">
          {/* Dark Theme Option */}
          <button
            onClick={() => setSelectedTheme('dark')}
            className={`w-full p-4 rounded-xl border-2 transition-all duration-200 ${
              selectedTheme === 'dark'
                ? 'border-purple-500 bg-purple-500/10'
                : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
            }`}
          >
            <div className="flex items-center gap-4">
              {/* Radio Circle */}
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                selectedTheme === 'dark' ? 'border-purple-500' : 'border-gray-600'
              }`}>
                {selectedTheme === 'dark' && (
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                )}
              </div>

              {/* Theme Preview */}
              <div className="flex-1 flex items-center gap-4">
                <div className="w-16 h-12 rounded-lg border flex flex-col gap-0.5 p-1.5 shadow-lg" style={{ backgroundColor: '#0a0a0a', borderColor: '#374151' }}>
                  <div className="flex-1 rounded" style={{ backgroundColor: '#1a1a1a' }}></div>
                  <div className="flex gap-0.5 h-2">
                    <div className="flex-1 rounded" style={{ backgroundColor: '#262626' }}></div>
                    <div className="flex-1 rounded" style={{ backgroundColor: '#262626' }}></div>
                  </div>
                </div>
                <div className="text-left flex-1">
                  <h3 className="text-white font-semibold">Dark Mode</h3>
                  <p className="text-gray-400 text-sm">Easy on the eyes in low light</p>
                </div>
              </div>

              {/* Checkmark */}
              {selectedTheme === 'dark' && (
                <svg className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </button>

          {/* Light Theme Option */}
          <button
            onClick={() => setSelectedTheme('light')}
            className={`w-full p-4 rounded-xl border-2 transition-all duration-200 ${
              selectedTheme === 'light'
                ? 'border-purple-500 bg-purple-500/10'
                : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
            }`}
          >
            <div className="flex items-center gap-4">
              {/* Radio Circle */}
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                selectedTheme === 'light' ? 'border-purple-500' : 'border-gray-600'
              }`}>
                {selectedTheme === 'light' && (
                  <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                )}
              </div>

              {/* Theme Preview */}
              <div className="flex-1 flex items-center gap-4">
                <div className="w-16 h-12 rounded-lg border flex flex-col gap-0.5 p-1.5 shadow-lg" style={{ backgroundColor: '#e8e3d5', borderColor: '#c4bcab' }}>
                  <div className="flex-1 rounded" style={{ backgroundColor: '#dfd9c8' }}></div>
                  <div className="flex gap-0.5 h-2">
                    <div className="flex-1 rounded" style={{ backgroundColor: '#d4cdb9' }}></div>
                    <div className="flex-1 rounded" style={{ backgroundColor: '#d4cdb9' }}></div>
                  </div>
                </div>
                <div className="text-left flex-1">
                  <h3 className="text-white font-semibold">Light Mode</h3>
                  <p className="text-gray-400 text-sm">Clean and bright interface</p>
                </div>
              </div>

              {/* Checkmark */}
              {selectedTheme === 'light' && (
                <svg className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </button>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-700">
          <button
            onClick={handleConfirm}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 rounded-lg font-semibold transition-all duration-200 shadow-lg shadow-purple-900/30"
            style={{ color: 'var(--color-text-on-accent)' }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
